import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from '../auth/AuthContext';
import { toast } from 'sonner';
import { Json } from "@/integrations/supabase/types";
import { Check, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface Plan {
  id: string;
  name: string;
  description: string;
  price: number;
  interval: string;
  credits: number;
  features: Record<string, boolean>;
  created_at?: string;
  stripe_price_id?: string;
}

interface Subscription {
  id: string;
  status: string;
  plan_id: string;
  current_period_end: string;
  is_trial?: boolean;
  trial_end?: string;
  plan?: {
    id: string;
    name: string;
    credits: number;
    price: number;
    interval: string;
  };
}

interface SubscriptionManagerProps {
  onSubscribe?: (planId: string) => void;
  currentSubscription?: Subscription | null;
  onUpdateSuccess?: () => void;
  selectedInterval?: 'month' | 'year';
}

const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({ 
  onSubscribe, 
  currentSubscription,
  onUpdateSuccess,
  selectedInterval = 'month'
}) => {
  const { user } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(currentSubscription || null);
  const [loading, setLoading] = useState(true);
  const [processingPlanId, setProcessingPlanId] = useState<string | null>(null);
  const [hasUsedTrial, setHasUsedTrial] = useState<boolean>(false);

  // Check if there's a plan display at the top of the page
  const getCurrentPlanName = () => {
    if (subscription?.status === 'trialing' && subscription?.plan) {
      return subscription.plan.name;
    }
    
    if (subscription?.status === 'active' && subscription?.plan) {
      return subscription.plan.name;
    }
    
    return 'Basic';
  };
  
  // Check if we're in a parent component that might be using a wrapper for current plan display
  React.useEffect(() => {
    // Find any elements that display "Basic" as the current plan and update them
    const currentPlanElements = document.querySelectorAll('[data-current-plan]');
    if (currentPlanElements.length > 0) {
      currentPlanElements.forEach(el => {
        el.textContent = getCurrentPlanName();
      });
    }
    
    // Check for a Basic text node inside a Current Plan section
    const currentPlanWrappers = document.querySelectorAll('.current-plan-display');
    if (currentPlanWrappers.length > 0) {
      currentPlanWrappers.forEach(wrapper => {
        const planNameEl = wrapper.querySelector('.plan-name');
        if (planNameEl && planNameEl.textContent === 'Basic') {
          planNameEl.textContent = getCurrentPlanName();
        }
      });
    }
  }, [subscription]);

  useEffect(() => {
    const fetchPlansAndSubscription = async () => {
      if (!user) return;

      try {
        // Fetch plans for the selected interval
        const { data: plansData, error: plansError } = await supabase
          .from('plans')
          .select('*')
          .eq('interval', selectedInterval)
          .order('price', { ascending: true });

        if (plansError) throw plansError;
        
        // Convert JSON features to Record<string, boolean>
        const formattedPlans = plansData?.map(plan => ({
          ...plan,
          features: typeof plan.features === 'string' 
            ? JSON.parse(plan.features as string)
            : (plan.features as Json) as Record<string, boolean>
        })) || [];
        
        setPlans(formattedPlans);

        // If we already have a subscription from props, use it
        if (currentSubscription) {
          setSubscription(currentSubscription);
        } else {
          // Otherwise fetch it - prioritize 'trialing' over 'active'
          //console.log('Fetching current subscription with priority for trial...');
          
          // First check for trialing subscriptions
          const { data: trialSubs, error: trialError } = await supabase
            .from('subscriptions')
            .select(`
              *,
              plan:plans(
                id,
                name,
                credits,
                price,
                interval
              )
            `)
            .eq('user_id', user.id)
            .eq('status', 'trialing')
            .order('created_at', { ascending: false });
            
          if (trialError) {
            //console.error('Error fetching trial subscriptions:', trialError);
          }
          
          // If we found trialing subscriptions, use the most recent one
          if (trialSubs && trialSubs.length > 0) {
            //console.log('Found trialing subscription:', trialSubs[0]);
            setSubscription(trialSubs[0]);
          } else {
            // Fall back to active subscription if no trial
            const { data: activeSub, error: activeError } = await supabase
              .from('subscriptions')
              .select(`
                *,
                plan:plans(
                  id,
                  name,
                  credits,
                  price,
                  interval
                )
              `)
              .eq('user_id', user.id)
              .eq('status', 'active')
              .maybeSingle();

            if (activeError && activeError.code !== 'PGRST116') {
              //console.error('Error fetching active subscription:', activeError);
            } else if (activeSub) {
              //console.log('Found active subscription:', activeSub);
              setSubscription(activeSub);
            } else {
              //console.log('No subscription found');
              setSubscription(null);
            }
          }
        }

        // Check if user has already used a trial
        const { data: trialHistory } = await supabase
          .from('trial_history')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        setHasUsedTrial(!!trialHistory);
      } catch (error) {
        // console.error('Error fetching data:', error);
        toast.error('Failed to load subscription data');
      } finally {
        setLoading(false);
      }
    };

    fetchPlansAndSubscription();
  }, [user, currentSubscription, selectedInterval]);

  // Update subscription state when currentSubscription prop changes
  useEffect(() => {
    if (currentSubscription) {
      setSubscription(currentSubscription);
    }
  }, [currentSubscription]);

  // Add debugging function
  const debugSupabaseFunction = async () => {
    // Removed debug function
  };

  // Call the debug function on mount
  useEffect(() => {
    // Removed debug logging
  }, [user]);

  const startFreeTrial = async () => {
    if (!user) {
      toast.error('You must be logged in to start a trial');
      return;
    }

    try {
      setProcessingPlanId('Premium-Trial');

      // Directly handle the startFreeTrial function
      const handleCreateTrial = async () => {
        try {
          // Use the supabase functions client
          const { data, error } = await supabase.functions.invoke('create-free-trial', {
            method: 'POST',
            body: { userId: user.id },
            headers: {
              'Content-Type': 'application/json'
            }
          });

          return { data, error };
        } catch (e) {
          // If we get a ReferenceError, try the alternative approach with fetch
          if (e instanceof ReferenceError && e.message.includes('process')) {
            // Get the current auth token
            const { data: { session } } = await supabase.auth.getSession();
            const token = session?.access_token;

            if (!token) {
              throw new Error('Authentication required. Please log in again.');
            }

            // Use the hardcoded Supabase URL
            const supabaseUrl = "https://jftsemrnjqbzinphyrpm.supabase.co";
            
            // Stringify the body first to ensure it's valid JSON
            const jsonBody = JSON.stringify({ userId: user.id });

            const response = await fetch(`${supabaseUrl}/functions/v1/create-free-trial`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json'
              },
              credentials: 'same-origin',
              mode: 'cors',
              body: jsonBody
            });

            const result = await response.json();
            
            return {
              data: result,
              error: response.ok ? null : { message: result.error || response.statusText }
            };
          }
          
          // Re-throw any other errors
          throw e;
        }
      };
      
      // Call the handler and process the result
      const { data, error } = await handleCreateTrial();
      
      if (error) {
        // Check for Stripe API key error
        if (error.message && error.message.includes('Invalid API Key')) {
          throw new Error('The trial system is currently unavailable. Please contact support or try again later.');
        }
        throw new Error(`Error starting trial: ${error.message}`);
      }

      if (data?.success) {
        toast.success('Your 14-day Premium trial has started!');
        
        // Wait for the subscription to be created in the database
        let attempts = 0;
        const maxAttempts = 5;
        
        const checkSubscription = async () => {
          try {
            // Use .order and .limit(1) to get only the most recent subscription
            const { data: subData, error } = await supabase
              .from('subscriptions')
              .select(`
                *,
                plan:plans(
                  id,
                  name,
                  credits,
                  price,
                  interval
                )
              `)
              .eq('user_id', user.id)
              .eq('status', 'trialing')
              .order('created_at', { ascending: false })
              .limit(1)
              .single();
              
            if (error) {
              // If error is about multiple rows, try again with a different approach
              if (error.code === 'PGRST110') {
                // Get all matching subscriptions
                const { data: allSubs, error: allSubsError } = await supabase
                  .from('subscriptions')
                  .select(`
                    *,
                    plan:plans(
                      id,
                      name,
                      credits,
                      price,
                      interval
                    )
                  `)
                  .eq('user_id', user.id)
                  .eq('status', 'trialing')
                  .order('created_at', { ascending: false });
                  
                if (allSubsError) {
                  return false;
                } else if (allSubs && allSubs.length > 0) {
                  // Use the most recent one
                  const mostRecent = allSubs[0];
                  
                  // Update subscription state with the most recent subscription
                  setSubscription(mostRecent);
                  
                  // If we have an onUpdateSuccess callback, call it
                  if (onUpdateSuccess) {
                    onUpdateSuccess();
                  }
                  
                  // Reset processing state
                  setProcessingPlanId(null);
                  return true;
                }
              }
            } else if (subData) {
              // Update subscription state with the new trial subscription
              setSubscription(subData);
            
              // If we have an onUpdateSuccess callback, call it
              if (onUpdateSuccess) {
                onUpdateSuccess();
              }
              
              // Reset processing state
              setProcessingPlanId(null);
              return true;
            }
            
            attempts++;
            
            if (attempts >= maxAttempts) {
              // Force update UI anyway
              setTimeout(() => {
                setProcessingPlanId(null);
                window.location.reload();
              }, 1000);
              return true;
            }
            
            return false;
          } catch (err) {
            return false;
          }
        };
        
        // Check every 2 seconds
        const checkInterval = setInterval(async () => {
          const found = await checkSubscription();
          if (found) {
            clearInterval(checkInterval);
          }
        }, 2000);
        
        // Set a timeout to clear the interval and reset UI
        setTimeout(() => {
          clearInterval(checkInterval);
          setProcessingPlanId(null);
        }, 15000); // 15 seconds max
        
      } else {
        throw new Error(data?.error || 'Failed to start trial');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to start trial');
      setProcessingPlanId(null);
    }
  };

  const handleSubscribe = async (displayName: string) => {
    if (!user) {
      toast.error('You must be logged in to subscribe');
      return;
    }
    
    try {
      setProcessingPlanId(displayName);
      
      // Find the plan from our database plans
      const plan = plans.find(p => p.name === displayName);
      
      if (!plan?.stripe_price_id || !plan.id) {
        throw new Error(`Selected plan not found or missing required information. Display name: ${displayName}`);
      }

      // Create a checkout session using the Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { 
          planId: plan.id,
          priceId: plan.stripe_price_id, 
          userId: user.id 
        }
      });
      
      if (error) {
        throw new Error(`Error creating checkout session: ${error.message}`);
      }
      
      if (data?.url) {
        // For Basic plan, handle without redirect
        if (displayName === 'Basic') {
          toast.success('You have successfully subscribed to the Basic plan');
          
          // If we have an onUpdateSuccess callback, call it
          if (onUpdateSuccess) {
            onUpdateSuccess();
          }
        } else {
          // Redirect to Stripe checkout for paid plans
          window.location.href = data.url;
        }
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create subscription');
    } finally {
      setProcessingPlanId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="w-8 h-8 border-4 border-roundone-purple border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const isCurrentPlan = (displayName: string) => {
    // If there's no subscription, then Basic is the current plan
    if (!subscription) {
      return displayName === 'Basic';
    }
    
    // For active subscriptions, check the plan name
    if ((subscription.status === 'active' || subscription.status === 'trialing') && subscription.plan) {
      return subscription.plan.name === displayName;
    }
    
    // If subscription is not active, treat as Basic plan
    return displayName === 'Basic';
  };

  const isTrialing = () => {
    return subscription?.status === 'trialing' || subscription?.is_trial === true;
  };

  const getTrialEndDate = () => {
    if (!subscription?.trial_end) return '';
    return new Date(subscription.trial_end).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const plansToRender = [
    {
      displayName: 'Basic',
      title: 'BASIC (FREE FOREVER)',
      price: 0,
      description: 'For startups & teams exploring AI-powered hiring.',
      features: [
        { name: '20 Candidate Assessments', included: true },
        { name: 'Create Unlimited Assessments', included: true },
        { name: 'ATS Integration', included: true },
        { name: 'AI Detector (Flags AI-generated responses)', included: true },
        { name: 'Single Sign-On (SSO)', included: false },
        { name: 'Custom Questions (Collect responses to specific questions)', included: false },
        { name: 'Custom Domain and Branding', included: false },
        { name: 'Priority Phone Support', included: false },
        { name: 'Dedicated Account Manager', included: false }
      ]
    },
    {
      displayName: 'Premium',
      title: 'PREMIUM',
      price: selectedInterval === 'month' ? 249 : 2490,
      description: 'For small TA teams focused on hiring top talent quickly',
      features: [
        { name: '100 Candidate Assessments', included: true },
        { name: 'Create Unlimited Assessments', included: true },
        { name: 'ATS Integration', included: true },
        { name: 'AI Detector (Flags AI-generated responses)', included: true },
        { name: 'Single Sign-On (SSO)', included: false },
        { name: 'Custom Questions (Collect responses to specific questions)', included: false },
        { name: 'Custom Domain and Branding', included: false },
        { name: 'Priority Phone Support', included: false },
        { name: 'Dedicated Account Manager', included: false }
      ]
    },
    {
      displayName: 'Agency',
      title: 'AGENCY',
      price: selectedInterval === 'month' ? 999 : 9990,
      description: 'For agencies and enterprises with high-volume hiring needs.',
      isPopular: true,
      features: [
        { name: '500 Candidate Assessments', included: true },
        { name: 'Create Unlimited Assessments', included: true },
        { name: 'ATS Integration', included: true },
        { name: 'AI Detector (Flags AI-generated responses)', included: true },
        { name: 'Single Sign-On (SSO)', included: true },
        { name: 'Custom Questions (Collect responses to specific questions)', included: true },
        { name: 'Custom Domain and Branding', included: true },
        { name: 'Priority Phone Support', included: true },
        { name: 'Dedicated Account Manager', included: true }
      ]
    }
  ];

  return (
    <div className="grid gap-4 md:grid-cols-3 max-w-5xl mx-auto">
      {plansToRender.map((plan) => (
        <div
          key={plan.displayName}
          className={`${
            plan.isPopular 
              ? 'bg-roundone-dark-purple rounded-2xl shadow-lg' 
              : 'bg-[#F8F7FF] rounded-2xl'
          } p-5 relative overflow-hidden flex flex-col`}
        >
          {plan.isPopular && (
            <div className="absolute top-2 right-2">
              <Badge className="bg-[#B197FC] text-white border-none uppercase text-xs font-medium px-3 py-0.5 rounded-full">
                Most Popular
              </Badge>
            </div>
          )}
          
          {isTrialing() && plan.displayName === 'Premium' && (
            <div className="absolute top-2 right-2">
              <Badge className="bg-green-500 text-white border-none uppercase text-xs font-medium px-3 py-0.5 rounded-full">
                Trial Active
              </Badge>
            </div>
          )}
          
          <div className="min-h-[140px]">
            <h3 className={`text-xs font-medium uppercase tracking-wide ${
              plan.isPopular ? 'text-white/80' : 'text-muted-foreground'
            }`}>
              {plan.title}
            </h3>

            <div className="flex items-baseline gap-1 mt-3">
              <span className={`text-4xl font-bold ${
                plan.isPopular ? 'text-white' : 'text-foreground'
              }`}>
                £{plan.price}
              </span>
              <span className={`text-xs ${
                plan.isPopular ? 'text-white/60' : 'text-muted-foreground'
              }`}>
                /month
              </span>
            </div>

            <p className={`text-xs mt-3 ${
              plan.isPopular ? 'text-white/60' : 'text-muted-foreground'
            }`}>
              {plan.description}
            </p>
            
            {isTrialing() && plan.displayName === 'Premium' && (
              <p className="text-xs mt-2 font-medium text-green-600">
                Trial ends: {getTrialEndDate()}
              </p>
            )}
          </div>

          <div className="flex-grow space-y-2">
            {plan.features.map((feature, index) => (
              <div key={index} className="flex items-start gap-2">
                {feature.included ? (
                  <Check className={`h-4 w-4 flex-shrink-0 ${
                    plan.isPopular ? 'text-white' : 'text-roundone-purple'
                  }`} />
                ) : (
                  <X className={`h-4 w-4 flex-shrink-0 ${
                    plan.isPopular ? 'text-white/40' : 'text-muted-foreground/40'
                  }`} />
                )}
                <span className={`text-xs ${
                  plan.isPopular 
                    ? feature.included ? 'text-white' : 'text-white/40'
                    : feature.included ? 'text-foreground' : 'text-muted-foreground/60'
                }`}>
                  {feature.name}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-5 space-y-2">
            {/* Regular subscription button */}
            <Button
              className={`w-full h-9 rounded-full text-sm font-medium ${
                isCurrentPlan(plan.displayName)
                  ? 'bg-gray-200 text-gray-700 hover:bg-gray-300 border-2 border-roundone-purple'
                  : plan.displayName === 'Basic'
                    ? 'bg-[#B197FC] text-white hover:bg-[#B197FC]/90'
                    : 'bg-roundone-purple text-white hover:bg-roundone-purple/90'
              }`}
              onClick={() => handleSubscribe(plan.displayName)}
              disabled={isCurrentPlan(plan.displayName) || processingPlanId === plan.displayName}
            >
              {processingPlanId === plan.displayName ? (
                <div className="flex items-center justify-center">
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Processing...
                </div>
              ) : isCurrentPlan(plan.displayName) ? (
                <div className="flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-1">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {isTrialing() && plan.displayName === 'Premium' ? 'Trial Active' : 'Current Plan'}
                </div>
              ) : plan.price > (subscription?.plan?.price || 0) ? (
                'Upgrade'
              ) : (
                'Downgrade'
              )}
            </Button>
            
            {/* Free trial button - only show for Premium plan when user is on Basic and hasn't used a trial */}
            {plan.displayName === 'Premium' && 
             !isCurrentPlan('Premium') && 
             isCurrentPlan('Basic') && 
             !hasUsedTrial && (
              <>
                {/* Regular button with form approach */}
                <Button
                  className="w-full h-9 rounded-full text-sm font-medium bg-green-500 text-white hover:bg-green-600"
                  onClick={async (e) => {
                    e.preventDefault();
                    if (!user) {
                      toast.error('You must be logged in to start a trial');
                      return;
                    }

                    setProcessingPlanId('Premium-Trial');
                    
                    try {
                      // Get the current auth token
                      const { data: { session } } = await supabase.auth.getSession();
                      const token = session?.access_token;

                      if (!token) {
                        throw new Error('Authentication required. Please log in again.');
                      }

                      // Call the edge function directly with proper auth
                      const response = await fetch(`https://jftsemrnjqbzinphyrpm.supabase.co/functions/v1/create-free-trial?userId=${encodeURIComponent(user.id)}`, {
                        method: 'GET',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        }
                      });
                      
                      // Check if response is HTML (trial success page)
                      const contentType = response.headers.get('content-type');
                      let result;
                      
                      if (contentType && contentType.includes('text/plain')) {
                        // This is the HTML success page, assume success
                        result = { success: true };
                      } else {
                        // Try to parse as JSON
                        result = await response.json();
                      }
                      
                      if (!response.ok) {
                        throw new Error(result.error || 'Failed to start trial');
                      }
                      
                      // Trial was successful, update UI
                      toast.success('Your 14-day Premium trial has started!');
                      
                      // Wait a moment for the DB to update, then fetch the latest subscription
                      let attempts = 0;
                      const maxAttempts = 5;
                      
                      const checkSubscription = async () => {
                        try {
                          const { data: subData, error } = await supabase
                            .from('subscriptions')
                            .select(`
                              *,
                              plan:plans(
                                id,
                                name,
                                credits,
                                price,
                                interval
                              )
                            `)
                            .eq('user_id', user.id)
                            .eq('status', 'trialing')
                            .order('created_at', { ascending: false })
                            .limit(1)
                            .single();
                            
                          if (error) {
                            // If error is about multiple rows, try again with a different approach
                            if (error.code === 'PGRST110') {
                              // Get all matching subscriptions
                              const { data: allSubs, error: allSubsError } = await supabase
                                .from('subscriptions')
                                .select(`
                                  *,
                                  plan:plans(
                                    id,
                                    name,
                                    credits,
                                    price,
                                    interval
                                  )
                                `)
                                .eq('user_id', user.id)
                                .eq('status', 'trialing')
                                .order('created_at', { ascending: false });
                                
                              if (allSubsError) {
                                return false;
                              } else if (allSubs && allSubs.length > 0) {
                                // Use the most recent one
                                const mostRecent = allSubs[0];
                                
                                // Update subscription state with the most recent subscription
                                setSubscription(mostRecent);
                                
                                // If we have an onUpdateSuccess callback, call it
                                if (onUpdateSuccess) {
                                  onUpdateSuccess();
                                }
                                
                                // Reset processing state
                                setProcessingPlanId(null);
                                return true;
                              }
                            }
                          } else if (subData) {
                            // Update subscription state with the new trial subscription
                            setSubscription(subData);
                          
                            // If we have an onUpdateSuccess callback, call it
                            if (onUpdateSuccess) {
                              onUpdateSuccess();
                            }
                            
                            // Reset processing state
                            setProcessingPlanId(null);
                            return true;
                          }
                          
                          attempts++;
                          
                          if (attempts >= maxAttempts) {
                            // Force update UI anyway
                            setTimeout(() => {
                              setProcessingPlanId(null);
                              window.location.reload();
                            }, 1000);
                            return true;
                          }
                          
                          return false;
                        } catch (err) {
                          return false;
                        }
                      };
                      
                      // Check every 2 seconds
                      const checkInterval = setInterval(async () => {
                        const found = await checkSubscription();
                        if (found) {
                          clearInterval(checkInterval);
                        }
                      }, 2000);
                      
                      // Set a timeout to clear the interval and reset UI
                      setTimeout(() => {
                        clearInterval(checkInterval);
                        setProcessingPlanId(null);
                      }, 15000); // 15 seconds max
                      
                    } catch (error: any) {
                      toast.error(error.message || 'Failed to start trial');
                      setProcessingPlanId(null);
                    }
                  }}
                  disabled={processingPlanId === 'Premium-Trial'}
                >
                  {processingPlanId === 'Premium-Trial' ? (
                    <div className="flex items-center justify-center">
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Processing...
                    </div>
                  ) : (
                    'Try Premium Free for 14 Days'
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SubscriptionManager;
